import { Alert } from "./alert";
import { Badge } from "./badge";

/**
 * Quellenband (Design-Guide `quellen-band.md`, Musterkatalog M-122, Betreiber 21.10.2026 "Q1"): zeigt oben auf einer Seite, aus welcher
 * Datenquelle sie gerade liest und ob diese Quelle auf dieser Umgebung bedient werden darf. Vier Zustände: echt, nicht echt
 * (Testinstanz oder Simulator, mit Hinweissatz), gesperrt/abgelaufen (Fehler-Alert, die Handlung ist gesperrt). Der Baustein
 * rechnet nichts: die Seite übergibt die fertig bewertete Quelle (Art, Erlaubnis aus der Quellensperre der Umgebung) und die Sätze.
 * Zustand nie nur Farbe: Titel, Badge-Wort und Satz tragen die Aussage.
 */
export type QuellenBandArt = "echt" | "testinstanz" | "simulator" | "mock" | "keine" | "unbekannt";

const TITEL: Record<QuellenBandArt, string> = {
  echt: "Echtdaten",
  testinstanz: "Testinstanz",
  simulator: "Simulator",
  mock: "Simulator (intern)",
  keine: "Keine Quelle",
  unbekannt: "Unbekannte Quelle",
};

const BESCHREIBUNG: Record<QuellenBandArt, string> = {
  echt: "Produktive HRWorks-Anbindung",
  testinstanz: "Testdaten aus der HRWorks-Testinstanz",
  simulator: "Erfundene Personen aus dem HRWorks-Simulator",
  mock: "Erfundene Personen aus dem eingebauten Simulator",
  keine: "Für diese Umgebung ist keine HRWorks-Quelle eingerichtet",
  unbekannt: "Der Host der HRWorks-Anbindung ist nicht bekannt",
};

export function QuellenBand({
  art,
  erlaubt,
  hinweis,
  sperrSatz,
  ablauf,
  letzterLauf,
  nurLesen = "HRWorks wird nur gelesen.",
  id,
}: {
  art: QuellenBandArt;
  /** Darf diese Quelle auf dieser Umgebung bedient werden (Quellensperre)? `false` = gesperrt: roter Satz, Titel mit Badge "gesperrt". */
  erlaubt: boolean;
  /** Satz bei nicht echter, erlaubter Quelle (gelb); bei echter oder gesperrter Quelle ungenutzt. */
  hinweis?: string;
  /** Satz bei gesperrter Quelle (rot). */
  sperrSatz?: string;
  /** Ablaufdatum der Testinstanz, wenn bekannt (fertig formatierter Text, z. B. "Läuft bis 30.11.2026 (noch 40 Tage)"); `abgelaufen` färbt rot. */
  ablauf?: { text: string; abgelaufen: boolean };
  /** Zeile unter dem Titel, fertig formatiert ("Letzter Abruf 21.10.2026, 09:14 · 27 Personen gelesen"). */
  letzterLauf?: string;
  nurLesen?: string;
  id?: string;
}) {
  const gesperrt = !erlaubt || (ablauf?.abgelaufen ?? false);
  const nichtEcht = art !== "echt";
  const beschreibung = ablauf ? ablauf.text : BESCHREIBUNG[art];
  return (
    <>
      {/* Preline: component/card/card-with-header (Karte) und component/alerts/alerts-soft-color-variants (Hinweissatz) */}
      <section aria-label="HRWorks-Quelle" id={id} className="bg-card border border-card-line shadow-2xs rounded-xl p-4 space-y-3">
        <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
          <span className="shrink-0 inline-flex justify-center items-center size-10 rounded-lg bg-surface border border-surface-line text-surface-foreground">
            <svg className="shrink-0 size-4" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <ellipse cx="12" cy="5" rx="9" ry="3" />
              <path d="M3 5V19A9 3 0 0 0 21 19V5" />
              <path d="M3 12A9 3 0 0 0 21 12" />
            </svg>
          </span>
          <div className="min-w-0 grow">
            <p className="text-xs text-muted-foreground-1">HRWorks-Quelle</p>
            <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
              <p className="font-semibold text-foreground">{TITEL[art]}</p>
              {gesperrt ? <Badge ton="fehler" punkt>gesperrt</Badge> : nichtEcht ? <Badge ton="warnung" punkt>nicht echt</Badge> : <Badge ton="erfolg" punkt>aktiv</Badge>}
              <p className="text-sm text-foreground">{beschreibung}</p>
            </div>
            {letzterLauf ? <p className="text-xs text-muted-foreground-1">{letzterLauf}</p> : null}
          </div>
          <p className="shrink-0 inline-flex items-center gap-x-1.5 text-xs text-muted-foreground-1">
            <svg className="shrink-0 size-3.5" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <rect width="18" height="11" x="3" y="11" rx="2" ry="2" />
              <path d="M7 11V7a5 5 0 0 1 10 0v4" />
            </svg>
            {nurLesen}
          </p>
        </div>
        {gesperrt && sperrSatz ? <Alert ton="fehler">{sperrSatz}</Alert> : null}
        {!gesperrt && nichtEcht && hinweis ? <Alert ton="warnung">{hinweis}</Alert> : null}
      </section>
    </>
  );
}
